import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { useDispatch } from "react-redux";
import { updatePluginToolbar } from "../../../common/actions";
import { editInteractive } from "./interactiveItemService";
import InteractiveCreditButton from "./InteractiveCreditButton";
import "./_interactiveItem.scss";

/* eslint-disable react/prop-types */

/**
 * "AI ile düzenle" — seçili kutunun pill menüsünden açılan prompt alanı.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * NEDEN AYRI BİR YOL — mini editör zaten var
 * ──────────────────────────────────────────────────────────────────────────
 * Mini editör VERİYİ düzenler: bir kartın yazısı, bir cevap anahtarı, bir
 * satırın silinmesi. Anında, kredisiz, modelsiz — ve bunların hiçbiri için AI
 * gerekmiyor.
 *
 * Burası KODU da değiştirebilen yol: "kartları daha renkli yap", "sürükleme
 * yerine tıklamayla olsun", "soruları zorlaştır". Bunlar bir formda
 * düzenlenemez çünkü formda alanı yok; modele söylenmesi gerekiyor. Ücretli
 * ve dakikalar sürüyor, bu yüzden mini editörün yerine değil YANINA duruyor —
 * bir harf hatası için buraya gelmek para ve zaman kaybı olurdu.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * SONUÇ KUTUYA UYGULANIR, ONAY KARTINA ÇIKMAZ
 * ──────────────────────────────────────────────────────────────────────────
 * Üretimde onay kartı var çünkü ortada henüz bir şey yok — yazar neye evet
 * dediğini görmeli. Burada kutu zaten sahnede: "şunu şöyle yap" dedikten
 * sonra ayrıca onaylatmak, yapılan işi iki kez sormak olur. Değişiklik
 * doğrudan uygulanır; beğenilmezse editörün geri-al'ı çalışır, çünkü yazılan
 * yer `updatePluginToolbar` (geri alınabilir reducer) ve ESKİ DOKÜMAN
 * SİLİNMEZ — her düzenleme yeni bir dosya yazar, geri alınan state eski
 * URL'ye döndüğünde o dosya hâlâ yerindedir.
 */
export default function InteractiveAiEdit({ boxId, state, onClose }) {
  const dispatch = useDispatch();
  const [instruction, setInstruction] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sourceBlobName = state?.sourceBlobName;
  // Bu özellikten önce üretilmiş kutular (ya da sidecar'ı yazılamamış olanlar)
  // düzenlenemez: modele mevcut kodu göstermeden "şunu değiştir" demek,
  // sıfırdan başka bir aktivite üretmek olurdu.
  const canEdit = !!sourceBlobName;

  const run = useCallback(async () => {
    if (!instruction.trim() || !canEdit) return;
    setBusy(true);
    setError(null);
    try {
      const result = await editInteractive({
        typeId: state?.typeId,
        instruction: instruction.trim(),
        sourceBlobName,
        item: state?.item,
        navId: state?.navId,
        llmModel: state?.model,
      });

      // Üç alan birlikte değişir: yeni doküman, yeni veri ve yeni kaynak.
      // Biri eksik yazılırsa kutu bir sonraki düzenlemede eski koda ya da
      // eski veriye bakar.
      if (result?.htmlUrl) {
        dispatch(updatePluginToolbar(boxId, "main", "state", "htmlUrl", result.htmlUrl, [], null));
      }
      if (result?.item) {
        dispatch(updatePluginToolbar(boxId, "main", "state", "item", result.item, [], null));
      }
      if (result?.sourceBlobName) {
        dispatch(
          updatePluginToolbar(
            boxId,
            "main",
            "state",
            "sourceBlobName",
            result.sourceBlobName,
            [],
            null
          )
        );
      }
      onClose?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }, [instruction, canEdit, state, sourceBlobName, dispatch, boxId, onClose]);

  return createPortal(
    // Kutunun İÇİNDEN açılıyor ve editör kutuları transform taşıyor:
    // transform'lu bir ata `position: fixed`'in referansını kendisi yapar ve
    // overlay o kutuya hapsolur. Portal bu yüzden.
    <div className="ii-editor" onClick={(e) => e.stopPropagation()}>
      <div className="ii-editor__panel ii-editor__panel--narrow">
        <header className="ii-editor__head">
          <div>
            <h2>{i18n.t("InteractiveItem.AiEdit", { defaultValue: "AI ile düzenle" })}</h2>
            <p>
              {i18n.t("InteractiveItem.AiEditHint", {
                defaultValue:
                  "Ne değişsin yaz — görünüm, içerik ya da davranış. Mevcut öğenin üzerine uygulanır.",
              })}
            </p>
          </div>
          <button type="button" className="ii-editor__x" onClick={onClose} disabled={busy}>
            ✕
          </button>
        </header>

        <div className="ii-editor__body">
          {!canEdit && (
            <div className="ii-editor__error">
              {i18n.t("InteractiveItem.AiEditUnavailable", {
                defaultValue:
                  "Bu öğe AI ile düzenlenemiyor (kaynağı yok). Yeni bir öğe üretebilirsin.",
              })}
            </div>
          )}
          {error && <div className="ii-editor__error">{error}</div>}

          <textarea
            ref={inputRef}
            className="ii-ai__input"
            rows={3}
            value={instruction}
            disabled={!canEdit || busy}
            placeholder={i18n.t("InteractiveItem.AiEditPlaceholder", {
              defaultValue:
                "Örn: kartları daha renkli yap · bir soru daha ekle · sürükleme yerine tıklamayla olsun",
            })}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              // Enter gönderir, Shift+Enter satır atlar — tek cümlelik bir
              // alanda her seferinde fareye gitmek yorucu.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                run();
              }
            }}
          />

          {busy && (
            <div className="ii-pane__busy">
              <div className="ii-pane__spinner" />
              <div>
                {i18n.t("InteractiveItem.AiEditRunning", {
                  defaultValue: "Değişiklik uygulanıyor",
                })}
              </div>
              <small>
                {i18n.t("InteractiveItem.GeneratingMeta", {
                  defaultValue:
                    "Birkaç dakika sürebilir — modalı kapatabilirsin, iş arka planda devam eder.",
                })}
              </small>
            </div>
          )}
        </div>

        <footer className="ii-editor__foot">
          {/* Düzenleme de üretim kadar ücretli ve aynı fiyat satırını okur —
              rakamı göstermek şart, yoksa kullanıcı bir cümle yazıp
              "Uygula"ya basmanın bedava olduğunu sanır. */}
          <div style={{ marginRight: "auto" }}>
            <InteractiveCreditButton
              llmModel={state?.model}
              disabled={busy || !canEdit}
              label={i18n.t("InteractiveItem.AiEdit", { defaultValue: "AI ile düzenle" })}
            />
          </div>
          <button type="button" className="ii-btn" onClick={onClose} disabled={busy}>
            {i18n.t("cancel") || "Vazgeç"}
          </button>
          <button
            type="button"
            className="ii-btn ii-btn--primary"
            onClick={run}
            disabled={!canEdit || busy || !instruction.trim()}
          >
            {i18n.t("InteractiveItem.AiEditApply", { defaultValue: "Uygula" })}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
